/**
 * Zeros of Functions generator.
 *
 * The zero of a linear function f(x) = m x + b is where f(x) = 0, i.e. the
 * x-intercept x = -b/m. Students solve algebraically (Z1/Z2) or read the
 * x-intercept from a rendered line (Z3). The zero is computed by solving the
 * LinearEquation m x + b = 0 and verified by substitution.
 *
 * Families:
 *   Z1: find the zero of f(x) = m x + b        -> number
 *   Z2: find the x-intercept of y = m x + b     -> number (same math, framed as intercept)
 *   Z3: read the x-intercept from a graph        -> number (coordinate plot with the line)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { LinearExpr, LinearEquation } from "../engine/linear";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";
import type { CoordinateModel } from "../graph/plot-model";
import { getTopic } from "../curriculum/curriculum";

const TOPIC = "zeros" as const;
const VERSION = "1.0.0";

type Family = "Z1" | "Z2" | "Z3";

interface Cfg {
  readonly families: readonly Family[];
  readonly mMax: number;
  readonly zeroMax: number;
  readonly pNeg: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["Z1", "Z2"], mMax: 4, zeroMax: 6, pNeg: 0.15 },
  standard: { families: ["Z1", "Z2", "Z3"], mMax: 6, zeroMax: 8, pNeg: 0.4 },
  hard: { families: ["Z1", "Z3"], mMax: 8, zeroMax: 9, pNeg: 0.5 },
  challenge: { families: ["Z1", "Z3"], mMax: 10, zeroMax: 9, pNeg: 0.5 },
};

function ruleLatex(m: Rational, b: Rational, lhs: string): string {
  const mPart = m.equals(rat(1)) ? "x" : m.equals(rat(-1)) ? "-x" : `${m.toLatex()}x`;
  const bPart = b.isZero() ? "" : b.isNegative() ? ` - ${b.neg().toLatex()}` : ` + ${b.toLatex()}`;
  return `${lhs} = ${mPart}${bPart}`;
}

/** The "m x" part with 1/-1 handling. */
function mVarLatex(m: Rational): string {
  return m.equals(rat(1)) ? "x" : m.equals(rat(-1)) ? "-x" : `${m.toLatex()}x`;
}

/** Steps for solving m x + b = 0 for the zero. */
function zeroSteps(
  m: Rational,
  b: Rational,
  zero: Rational,
): { description: string; latex: string }[] {
  const mVar = mVarLatex(m);
  const bSigned = b.isNegative() ? `- ${b.neg().toLatex()}` : `+ ${b.toLatex()}`;
  const movedB = b.neg(); // right side after moving b across
  const steps = [
    {
      description: "A zero is an input that makes the output 0. Set the rule equal to 0.",
      latex: `${mVar} ${bSigned} = 0`,
    },
  ];
  if (!b.isZero()) {
    steps.push({
      description: `Move the constant: ${b.isNegative() ? "add" : "subtract"} ${b.abs().toLatex()} on both sides.`,
      latex: `${mVar} = ${movedB.toLatex()}`,
    });
  }
  steps.push({
    description: `Divide both sides by ${m.toLatex()} to isolate x.`,
    latex: `x = \\frac{${movedB.toLatex()}}{${m.toLatex()}} = ${zero.toLatex()}`,
  });
  return steps;
}

/** A small coordinate plot of the line y = m x + b with its x-intercept marked. */
function lineGraph(m: Rational, b: Rational, zero: Rational): CoordinateModel {
  const zeroX = zero.toNumber();
  const span = 7;
  const xMin = Math.floor(zeroX) - span;
  const xMax = Math.ceil(zeroX) + span;
  const yAt = (x: number) => m.mul(rat(x)).add(b).toNumber();
  const yA = yAt(xMin);
  const yB = yAt(xMax);
  const yMin = Math.floor(Math.min(yA, yB, 0)) - 1;
  const yMax = Math.ceil(Math.max(yA, yB, 0)) + 1;
  return {
    kind: "coordinate",
    xMin,
    xMax,
    yMin,
    yMax,
    points: [{ x: zero, y: rat(0), closed: true }],
    segments: [
      {
        from: { x: rat(xMin), y: m.mul(rat(xMin)).add(b) },
        to: { x: rat(xMax), y: m.mul(rat(xMax)).add(b) },
      },
    ],
  };
}

export class ZerosGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const m = rat(rng.int(1, cfg.mMax) * rng.sign(cfg.pNeg));
      // Choose an integer zero, then set b = -m*zero so f(zero)=0.
      const zero = rat(rng.intNonZero(-cfg.zeroMax, cfg.zeroMax));
      const b = m.mul(zero).neg();
      const rule = new LinearExpr(m, b);

      // Verify the zero independently.
      const eq = new LinearEquation(rule, LinearExpr.constantExpr(rat(0)));
      if (!eq.solve().value?.equals(zero)) continue;

      let promptText: string;
      let promptLatex: string;
      let graph: CoordinateModel | undefined;

      if (family === "Z1") {
        promptText = `Find the zero of ${ruleLatex(m, b, "f(x)")}.`;
        promptLatex = `\\text{Find the zero of } ${ruleLatex(m, b, "f(x)")}.`;
      } else if (family === "Z2") {
        promptText = `Find the x-intercept of ${ruleLatex(m, b, "y")}.`;
        promptLatex = `\\text{Find the x-intercept of } ${ruleLatex(m, b, "y")}.`;
      } else {
        promptText = "What is the x-intercept (zero) shown on the graph?";
        promptLatex = "\\text{What is the x-intercept (zero) shown on the graph?}";
        graph = lineGraph(m, b, zero);
      }

      const q: Question = {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText, latex: promptLatex },
        answerType: "number",
        answer: { kind: "number", value: zero },
        steps:
          family === "Z3"
            ? [
                {
                  description:
                    "The zero is where the line crosses the x-axis. Read that x-value from the graph.",
                  latex: `x = ${zero.toLatex()}`,
                },
              ]
            : zeroSteps(m, b, zero),
        hints: [
          { level: 1, text: "A zero (x-intercept) is where the output is 0." },
          { level: 2, text: "Set the rule equal to 0 and solve for x." },
        ],
        subskills: topic.subskills,
      };
      return graph ? { ...q, graph } : q;
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const zeros = new ZerosGenerator();
